import React, { useMemo, useState } from "react";
import Icon from "./icons";
import { sanitizeAnswerHtml } from "../../../lib/artemis/sanitize";
import { timeLabel } from "../../../lib/artemis/present";
import {
  ALERT_FILTERS, alertFacts, alertLink, alertMatches, alertMessageKind, alertPlace, alertRuleLine, alertSentLine, changeExtras,
  checkedLine, emailOffText, offeredChannels, settingsPayload
} from "../../../lib/artemis/alerts";
import { AlertDefaults, AlertEditor, Switch } from "./alertsView";

// The hub's Alerts section (WEB-7169, Option B): the alerts conversation's messages as the list, the chosen
// alert in the centre with the alert that wrote it, and the watches and where alerts go on the right.
// The canvas "Artemis Redesign Options", boards "B · Web hub" and "Shared components".

const escapeHtml = text => String(text || '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

// An alert message's text without its title, which the header shows: Html opens with the title in bold.
const bodyHtml = turn => {
  const html = turn.html || String(turn.text || '').split(/\n{2,}/).slice(1).map(p => `<p>${escapeHtml(p)}</p>`).join('')
  return sanitizeAnswerHtml(html.replace(/^\s*<p>\s*<strong>[^<]*<\/strong>\s*<\/p>/i, ''))
}

const titleOf = turn => (turn.alert && turn.alert.Title) || String(turn.text || '').split('\n')[0] || 'Alert'

// The list column. turns: the alerts conversation's alert messages, oldest first; unread: their ids.
export const AlertList = ({ turns, unread, selectedId, filter, onFilter, records, onSelect, onMarkAll, sub, loading }) => {
  const shown = [...turns].reverse().filter(t => alertMatches(t, filter, records))
  return (
    <aside className="artemis-panel artemis-hub-list" aria-label="Alerts">
      <div className="artemis-hub-list-head">
        <span className="artemis-hub-list-titles">
          <span className="artemis-hub-list-title">Alerts</span>
          <span className="artemis-hub-list-sub">{sub}</span>
        </span>
        {unread.size > 0 && <button type="button" className="artemis-small-button artemis-hub-mark-read" onClick={onMarkAll}>Mark all read</button>}
      </div>
      <div className="artemis-hub-list-tools" role="group" aria-label="Show">
        {ALERT_FILTERS.map(f => (
          <button key={f.key} type="button" className={`artemis-hub-chip ${filter === f.key ? 'artemis-hub-chip-on' : ''}`} aria-pressed={filter === f.key}
            onClick={() => onFilter(f.key)}>{f.label}</button>
        ))}
      </div>
      <div className="artemis-hub-list-body">
        {shown.length === 0 && (
          <p className="artemis-muted artemis-hub-empty">
            {loading ? 'Loading…'
              : filter === 'watches' ? 'Nothing from your watches yet. Ask in any chat: “watch unit 4 for closures”.'
              : filter === 'finished' ? 'Nothing from finished alerts.'
              : 'Alerts Artemis sends you show up here. Ask in any chat: “remind me 2 weeks before my next draw closes”.'}
          </p>
        )}
        {shown.map(t => {
          const kind = alertMessageKind(t.alert)
          const isUnread = unread.has(t.messageId)
          return (
            <div key={t.id} className={`artemis-hub-row-wrap ${t.messageId === selectedId ? 'artemis-hub-row-on' : ''}`}>
              <button type="button" className="artemis-hub-row" aria-current={t.messageId === selectedId || undefined} onClick={() => onSelect(t.messageId)}>
                <span className={`artemis-hub-glyph ${isUnread && !t.notice ? 'artemis-hub-glyph-alert' : ''}`} aria-hidden="true"><Icon name={kind.icon} size={16} /></span>
                <span className="artemis-hub-row-text">
                  <span className={`artemis-hub-row-title ${t.notice ? 'artemis-hub-row-plain' : ''}`}>{titleOf(t)}</span>
                  <span className="artemis-hub-row-sub">{[kind.label, kind.kind === 'warning' && alertPlace(t.alert.Payload), timeLabel(t.at)].filter(Boolean).join(' · ')}</span>
                </span>
                {isUnread && <span className="artemis-hub-unread-dot" aria-label="Unread" />}
              </button>
            </div>
          )
        })}
      </div>
    </aside>
  )
}

// The "This reminder" row: the alert that wrote the message, with Edit, Pause or Resume, and Delete.
const AlertRule = ({ record, offered, onUpdate, onRemove }) => {
  const [mode, setMode] = useState(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const watch = record && record.Kind === 'change'
  const running = record && (record.Status === 'active' || record.Status === 'paused')
  const run = (work, fallback) => {
    setBusy(true)
    setProblem(null)
    return work().catch(err => setProblem((err && err.userMessage) || fallback)).finally(() => setBusy(false))
  }
  if (record && mode === 'delete') {
    return (
      <div className="artemis-hub-rule artemis-hub-rule-confirm" role="group" aria-label={`Delete ${record.Title}`}>
        <span className="artemis-hub-rule-text">
          <span className="artemis-hub-rule-title">Delete this {watch ? 'watch' : 'reminder'}?</span>
          <span className="artemis-hub-rule-sub">{record.Title}. Nothing is deleted until you say yes.</span>
        </span>
        <button type="button" className="artemis-hub-action artemis-hub-action-go" disabled={busy}
          onClick={() => run(() => onRemove(record.Id), 'Couldn’t delete it. Try again.').then(() => setMode(null))}>Yes, delete</button>
        <button type="button" className="artemis-hub-action" onClick={() => setMode(null)}>Keep it</button>
      </div>
    )
  }
  return (
    <div className="artemis-hub-rule-wrap">
      <div className="artemis-hub-rule">
        <span className="artemis-hub-rule-text">
          <span className="artemis-hub-rule-title">{watch ? 'This watch' : 'This reminder'}</span>
          <span className="artemis-hub-rule-sub">{alertRuleLine(record)}</span>
        </span>
        {record && running && (
          <>
            <button type="button" className="artemis-hub-action artemis-hub-action-edit" aria-expanded={mode === 'edit'} onClick={() => setMode(m => (m === 'edit' ? null : 'edit'))}>Edit</button>
            <button type="button" className="artemis-hub-action" disabled={busy}
              onClick={() => run(() => onUpdate(record.Id, { Paused: record.Status === 'active' }), 'Couldn’t change it. Try again.')}>
              {record.Status === 'active' ? 'Pause' : 'Resume'}
            </button>
          </>
        )}
        {record && !running && (
          <button type="button" className="artemis-hub-action" disabled={busy}
            onClick={() => run(() => onUpdate(record.Id, { Paused: false }), 'Couldn’t resume it. Try again.')}>Resume</button>
        )}
        {record && <button type="button" className="artemis-hub-action" onClick={() => setMode('delete')}>Delete</button>}
      </div>
      {problem && <p className="artemis-field-error" role="alert">{problem}</p>}
      {record && mode === 'edit' && (
        <AlertEditor alert={record} offered={offered} onCancel={() => setMode(null)} onSave={patch => onUpdate(record.Id, patch).then(() => setMode(null))} />
      )}
    </div>
  )
}

// The centre's header for the chosen alert: its kind's tile, title, what it is and where it went, and its link.
export const AlertHeader = ({ turn, record, now }) => {
  const kind = alertMessageKind(turn.alert)
  const link = alertLink(turn.alert)
  return (
    <>
      <span className={`artemis-hub-alert-tile ${turn.notice ? 'artemis-hub-alert-tile-plain' : ''}`} aria-hidden="true"><Icon name={kind.icon} size={20} /></span>
      <span className="artemis-header-text">
        <span className="artemis-header-title"><span className="artemis-hub-title">{titleOf(turn)}</span></span>
        <span className="artemis-header-meta">{turn.notice ? `A note from Artemis · ${timeLabel(turn.at, now)}` : alertSentLine(turn.alert, turn.at, record, now)}</span>
      </span>
      {link && (
        <a className="artemis-hub-alert-link" href={link.url} target="_blank" rel="noopener noreferrer">
          {link.label}<Icon name="external" size={14} />
        </a>
      )}
    </>
  )
}

// The chosen alert: its text, three facts from its Payload, and the alert that wrote it. A notice is a plain
// paragraph from Artemis. children: the questions asked about it, rendered by the chat.
export const AlertDetail = ({ turn, record, alerts, now, children }) => {
  const html = useMemo(() => bodyHtml(turn), [turn.html, turn.text])
  const offered = offeredChannels(alerts.settings && alerts.settings.AvailableChannels)
  if (turn.notice) {
    return (
      <div className="artemis-hub-alert" data-message-id={turn.messageId}>
        <div className="chat-answer artemis-answer artemis-hub-alert-text" dangerouslySetInnerHTML={{ __html: sanitizeAnswerHtml(turn.html || escapeHtml(turn.text)) }} />
        {children}
      </div>
    )
  }
  const facts = alertFacts(turn.alert, record, now)
  const change = changeExtras(turn.alert)
  return (
    <div className="artemis-hub-alert" data-message-id={turn.messageId}>
      {change && change.summary
        ? <p className="artemis-hub-alert-summary">{change.summary}</p>
        : <div className="chat-answer artemis-answer artemis-hub-alert-text" dangerouslySetInnerHTML={{ __html: html }} />}
      {facts.length > 0 && (
        <div className="artemis-hub-facts">
          {facts.map(f => (
            <div key={f.k} className="artemis-hub-fact"><span className="artemis-hub-fact-k">{f.k}</span><span className="artemis-hub-fact-v">{f.v}</span></div>
          ))}
        </div>
      )}
      {turn.alert && turn.alert.AlertId && (
        <AlertRule key={turn.alert.AlertId} record={record} offered={offered} onUpdate={alerts.update} onRemove={alerts.remove} />
      )}
      {children}
    </div>
  )
}

const hhmm = text => {
  const m = /^(\d{2}):(\d{2})$/.exec(text || '')
  if (!m) return text
  const h = Number(m[1])
  return `${h % 12 || 12}${m[2] === '00' ? '' : `:${m[2]}`} ${h < 12 ? 'am' : 'pm'}`
}

// The right column: every running alert with a switch (the one behind the chosen message outlined), and
// where alerts go. The channel switches set the defaults for new alerts (PUT Alerts/Settings); quiet hours
// and the time zone open WEB-7145's form.
export const AlertsRight = ({ alerts, selectedAlertId, onPickAlert, onVerifyNumber }) => {
  const [editing, setEditing] = useState(false)
  const [problem, setProblem] = useState(null)
  const [busy, setBusy] = useState(null)
  const settings = alerts.settings
  const available = (settings && settings.AvailableChannels) || []
  const channels = (settings && settings.Channels) || ['inbox']
  const running = alerts.items.filter(a => a.Status === 'active' || a.Status === 'paused')
  const run = (key, work, fallback) => {
    setBusy(key)
    setProblem(null)
    return work().catch(err => setProblem((err && err.userMessage) || fallback)).finally(() => setBusy(null))
  }
  const setChannel = (key, on) => run(key, () => alerts.saveSettings(settingsPayload({
    channels: on ? [...channels, key] : channels.filter(k => k !== key),
    timeZone: settings.TimeZone, quietStart: settings.QuietHoursStart, quietEnd: settings.QuietHoursEnd
  })), 'Couldn’t save that. Try again.')
  const emailOff = settings && emailOffText(settings.EmailOff)
  const zone = settings && settings.TimeZone ? settings.TimeZone.split('/').pop().replace(/_/g, ' ') : null
  return (
    <aside className="artemis-panel artemis-hub-right artemis-hub-alerts-right" aria-label="Watches and where alerts go">
      <div className="artemis-hub-right-scroll">
        <span className="artemis-hub-right-title">Watches and reminders</span>
        {alerts.error && <p className="artemis-muted" role="status">{alerts.error}. <button type="button" className="artemis-link-button" onClick={alerts.refresh}>Try again</button></p>}
        <div className="artemis-hub-watches">
          {alerts.loaded && running.length === 0 && <p className="artemis-muted">No watches or reminders running.</p>}
          {running.map(a => (
            <div key={a.Id} className={`artemis-hub-watch ${a.Id === selectedAlertId ? 'artemis-hub-watch-on' : ''}`}>
              <button type="button" className="artemis-hub-watch-text" onClick={() => onPickAlert(a)} disabled={!a.LastMessage}
                title={a.LastMessage ? 'Show its last alert' : undefined}>
                <span className={`artemis-hub-watch-title ${a.Status === 'paused' ? 'artemis-hub-watch-paused' : ''}`}>{a.Title}</span>
                <span className="artemis-hub-watch-sub">{a.Status === 'paused' ? 'Paused' : checkedLine(a) || alertRuleLine(a).split(' · ')[0]}</span>
              </button>
              <Switch on={a.Status === 'active'} label={`${a.Status === 'active' ? 'Pause' : 'Resume'} ${a.Title}`} disabled={busy === a.Id}
                onChange={on => run(a.Id, () => alerts.update(a.Id, { Paused: !on }), 'Couldn’t change it. Try again.')} />
            </div>
          ))}
        </div>
        <p className="artemis-hub-note">New watches are set up in any chat: “watch unit 4 for closures” or “remind me a week before Wyoming closes”.</p>

        <span className="artemis-hub-right-title">Where alerts go</span>
        {settings && (
          <div className="artemis-hub-channels">
            <div className="artemis-hub-channel"><span>Inbox</span><span className="artemis-hub-channel-v">Always</span></div>
            {available.includes('push') && (
              <div className="artemis-hub-channel"><span>Push</span>
                <Switch on={channels.includes('push')} label="Push" disabled={busy === 'push'} onChange={on => setChannel('push', on)} /></div>
            )}
            {available.includes('email') && (
              <div className="artemis-hub-channel"><span>Email</span>
                <Switch on={channels.includes('email')} label="Email" disabled={busy === 'email'} onChange={on => setChannel('email', on)} /></div>
            )}
            {available.includes('sms') && (
              <div className="artemis-hub-channel"><span>Text</span>
                {onVerifyNumber
                  ? <button type="button" className="artemis-link-button artemis-hub-channel-link" onClick={onVerifyNumber}>Verify number</button>
                  : <span className="artemis-hub-channel-v">Verify your number</span>}
              </div>
            )}
            <div className="artemis-hub-channel">
              <span>Quiet hours</span>
              <button type="button" className="artemis-link-button artemis-hub-channel-link" aria-expanded={editing} onClick={() => setEditing(e => !e)}>
                {settings.QuietHoursStart && settings.QuietHoursEnd
                  ? `${hhmm(settings.QuietHoursStart)} – ${hhmm(settings.QuietHoursEnd)}${zone ? `, ${zone}` : ''}`
                  : `None${zone ? ` · ${zone}` : ''}`}
              </button>
            </div>
          </div>
        )}
        {emailOff && <p className="artemis-dictation-note" role="status">{emailOff}</p>}
        {problem && <p className="artemis-field-error" role="alert">{problem}</p>}
        {settings && editing && <AlertDefaults settings={settings} offered={offeredChannels(available)} onSave={alerts.saveSettings} />}
      </div>
    </aside>
  )
}
